import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { ReplayRow, ReplaySegment } from './computeReplaySegments';
import { getReplayBucketColor, ReplayBucket } from './replayBuckets';
import { HighlightedLoad } from './replayInteractions';
import { computeReplayTicks } from './replayTicks';

// Fixed left column (truck label) and right gutter, in px. The axis ticks, the
// scrubber overlay, and the per-row tracks all share these so they stay aligned.
const LABEL_WIDTH_IN_PX = 150;
const TRACK_RIGHT_GUTTER_IN_PX = 14;
const ROW_HEIGHT_IN_PX = 30;
const TRACK_HEIGHT_IN_PX = 22;
// Zero/short segments still need to be clickable and visible.
const MIN_BAR_WIDTH_PCT = 0.4;
// Only label a bar with its minutes when it's wide enough to fit the text.
const MIN_BAR_PCT_FOR_MINUTES = 3;
// Opacity for rows/segments that aren't the current selection/highlight.
const DIMMED_OPACITY = 0.26;
// Diagonal hatch for the no-signal/unknown bucket (matches the design mockup),
// so it reads distinctly from solid Idle.
const UNKNOWN_HATCH =
  'repeating-linear-gradient(45deg, #CFD8DD, #CFD8DD 3px, #EEF3F6 3px, #EEF3F6 6px)';
// Scrubber line color (design mockup).
const SCRUBBER_COLOR = '#EA1200';
const LOAD_BAR_ATTRIBUTE = 'data-replay-load-bar';
// A mouse drifts a pixel or two between press and release; a shorter move is a click.
const DRAG_THRESHOLD_IN_PX = 3;
// Resolution of the playback slider — 1000 steps across the window is smooth
// enough for scrubbing without flooding state updates.
const SCRUBBER_STEP = 0.001;

const BUCKET_LABEL_KEYS: Record<ReplayBucket, string> = {
  AT_DROPOFF: 'truck_replay.bucket.at_dropoff',
  AT_PICKUP: 'truck_replay.bucket.at_pickup',
  EN_ROUTE: 'truck_replay.bucket.en_route',
  IDLE: 'truck_replay.bucket.idle',
  UNKNOWN: 'truck_replay.bucket.no_signal',
};

interface TruckReplayGanttProps {
  highlightedLoad: HighlightedLoad | null;
  isLocked: boolean;
  isPlaying: boolean;
  maxTimeMs: number;
  minTimeMs: number;
  onHighlightLoad: (load: HighlightedLoad) => void;
  onLockChange: (isLocked: boolean) => void;
  onScrubFraction: (fraction: number) => void;
  onSelectTruck: (jobId: string) => void;
  onTogglePlay: () => void;
  replayFraction: number;
  replayTimeMs: number;
  rows: ReplayRow[];
  selectedJobId: string | null;
}

function segmentBackground(bucket: ReplayBucket) {
  return bucket === 'UNKNOWN' ? UNKNOWN_HATCH : getReplayBucketColor(bucket);
}

const TruckReplayGantt = ({
  highlightedLoad,
  isLocked,
  isPlaying,
  maxTimeMs,
  minTimeMs,
  onHighlightLoad,
  onLockChange,
  onScrubFraction,
  onSelectTruck,
  onTogglePlay,
  replayFraction,
  replayTimeMs,
  rows,
  selectedJobId,
}: TruckReplayGanttProps) => {
  const theme = useTheme();
  const areaRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Pointer capture retargets later events, so the release cannot tell what was pressed.
  const pressedLoadBar = useRef(false);
  const pressDragged = useRef(false);
  const pressOrigin = useRef({ x: 0, y: 0 });
  // Hover/drag scrubbing fires on every pointer move; coalesce to one update per
  // animation frame so marker interpolation across all trucks stays frame-bounded.
  const pendingScrub = useRef<{ clientX: number; ignoreOutsideTrack: boolean } | null>(
    null,
  );
  const scrubFrameId = useRef<number | null>(null);

  const span = maxTimeMs - minTimeMs;
  const ticks = computeReplayTicks(minTimeMs, maxTimeMs);

  // Raw 0..1 position of a clientX within the track. Returns null when the
  // pointer is outside the plotted track (over the label gutter or right gutter)
  // or before layout — value may fall below 0 / above 1 inside the gutters.
  const trackFractionFromClientX = (clientX: number) => {
    const element = areaRef.current;
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const trackWidth = rect.width - LABEL_WIDTH_IN_PX - TRACK_RIGHT_GUTTER_IN_PX;
    if (trackWidth <= 0) return null;

    return (clientX - rect.left - LABEL_WIDTH_IN_PX) / trackWidth;
  };

  const applyPendingScrub = () => {
    scrubFrameId.current = null;
    const pending = pendingScrub.current;
    pendingScrub.current = null;
    if (!pending) return;

    const fraction = trackFractionFromClientX(pending.clientX);
    if (fraction === null) return;
    // A bare hover over the label/right gutter must not snap the scrubber to an
    // end; only press-drag (ignoreOutsideTrack = false) clamps to 0/1.
    if (pending.ignoreOutsideTrack && (fraction < 0 || fraction > 1)) return;

    onScrubFraction(Math.min(1, Math.max(0, fraction)));
  };

  const scheduleScrub = (clientX: number, ignoreOutsideTrack: boolean) => {
    pendingScrub.current = { clientX, ignoreOutsideTrack };
    if (scrubFrameId.current !== null) return;
    scrubFrameId.current = requestAnimationFrame(applyPendingScrub);
  };

  const cancelPendingScrub = () => {
    if (scrubFrameId.current !== null) {
      cancelAnimationFrame(scrubFrameId.current);
      scrubFrameId.current = null;
    }
    pendingScrub.current = null;
  };

  useEffect(() => {
    return () => {
      cancelPendingScrub();
    };
  }, []);

  return (
    <Box>
      {/* Rows + scrubber overlay (time axis lives at the bottom, with the slider) */}
      <Box
        ref={areaRef}
        aria-label={t(isLocked ? 'truck_replay.unlock_time' : 'truck_replay.lock_time')}
        onPointerDown={(event) => {
          setIsDragging(true);
          event.currentTarget.setPointerCapture(event.pointerId);
          pressedLoadBar.current =
            event.target instanceof Element &&
            event.target.closest(`[${LOAD_BAR_ATTRIBUTE}]`) !== null;
          pressDragged.current = false;
          pressOrigin.current = { x: event.clientX, y: event.clientY };
          // A click/press jumps to that time (clamped), even over the gutters.
          scheduleScrub(event.clientX, false);
        }}
        onPointerMove={(event) => {
          if (isDragging && !pressDragged.current) {
            const { x, y } = pressOrigin.current;
            pressDragged.current =
              Math.hypot(event.clientX - x, event.clientY - y) > DRAG_THRESHOLD_IN_PX;
          }
          // While dragging, clamp to the ends; a bare hover scrubs only within
          // the plotted track (hovering the labels/gutter is ignored).
          if (isDragging || !isLocked) scheduleScrub(event.clientX, !isDragging);
        }}
        onPointerUp={() => {
          // The truck label stops the press, so its release is not ours.
          if (!isDragging) return;

          setIsDragging(false);
          if (pressedLoadBar.current) return;

          onLockChange(pressDragged.current ? true : !isLocked);
        }}
        onPointerLeave={() => {
          if (!isDragging) cancelPendingScrub();
        }}
        sx={{
          position: 'relative',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <Box
          sx={{
            backgroundColor: SCRUBBER_COLOR,
            bottom: 8,
            left: `calc(${LABEL_WIDTH_IN_PX}px + ${replayFraction} * (100% - ${
              LABEL_WIDTH_IN_PX + TRACK_RIGHT_GUTTER_IN_PX
            }px))`,
            pointerEvents: 'none',
            position: 'absolute',
            top: -4,
            width: isLocked ? '4px' : '2px',
            zIndex: 5,
          }}
        >
          {isLocked ? (
            <Box
              sx={{
                backgroundColor: SCRUBBER_COLOR,
                borderRadius: '50%',
                height: 9,
                left: '50%',
                position: 'absolute',
                top: -5,
                transform: 'translateX(-50%)',
                width: 9,
              }}
            />
          ) : null}
        </Box>

        {rows.map((row) => (
          <GanttRow
            key={row.key}
            highlightedLoad={highlightedLoad}
            minTimeMs={minTimeMs}
            onHighlightLoad={onHighlightLoad}
            onSelectTruck={onSelectTruck}
            row={row}
            rowDimmed={selectedJobId !== null && selectedJobId !== row.key}
            span={span}
          />
        ))}
      </Box>

      {/* Bottom timeline: time-axis ticks sit directly above the slider, and the
          slider spans exactly the plotted track (same offsets as the rows) so its
          handle aligns with the on-chart indicator and the trucks on the map. */}
      <Box sx={{ mt: 1 }}>
        {/* Time-axis ticks, aligned to the track */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: `0 0 ${LABEL_WIDTH_IN_PX}px` }} />
          <Box
            sx={{
              color: theme.brandV2.colors.treadGray3,
              flex: 1,
              fontSize: 11,
              height: 18,
              mr: `${TRACK_RIGHT_GUTTER_IN_PX}px`,
              position: 'relative',
            }}
          >
            {ticks.map((tick) => (
              <Box
                key={tick.fraction}
                sx={{
                  left: `${tick.fraction * 100}%`,
                  position: 'absolute',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                {tick.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Play + time in the label column, slider across the track */}
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: `0 0 ${LABEL_WIDTH_IN_PX}px`,
              gap: 0.5,
              pr: 1.5,
            }}
          >
            <IconButton
              aria-label={t(isPlaying ? 'truck_replay.pause' : 'truck_replay.play')}
              onClick={onTogglePlay}
              size="small"
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Typography
              variant="caption"
              sx={{ color: theme.brandV2.colors.treadBlack, flex: 1, textAlign: 'right' }}
            >
              {dayjs.tz(replayTimeMs).format('h:mm A')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, mr: `${TRACK_RIGHT_GUTTER_IN_PX}px` }}>
            <Slider
              aria-label={t('truck_replay.replay_time')}
              max={1}
              min={0}
              onChange={(_, value) => onScrubFraction(value as number)}
              size="small"
              step={SCRUBBER_STEP}
              sx={{
                color: theme.brandV2.colors.treadBlue,
                display: 'block',
                py: 1,
                '& .MuiSlider-rail': {
                  backgroundColor: theme.brandV2.colors.treadGray7,
                  height: 4,
                  opacity: 1,
                },
                '& .MuiSlider-track': { border: 'none', height: 4 },
                '& .MuiSlider-thumb': {
                  backgroundColor: '#FFFFFF',
                  border: `2px solid ${theme.brandV2.colors.treadBlue}`,
                  height: 14,
                  width: 14,
                  // v2 focus/active halo — matches treadBlue at low alpha.
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0 0 0 6px ${theme.brandV2.colors.treadBlue}29`,
                  },
                  '&.Mui-active': {
                    boxShadow: `0 0 0 8px ${theme.brandV2.colors.treadBlue}29`,
                  },
                },
              }}
              value={replayFraction}
            />
          </Box>
        </Box>
      </Box>

      <Legend />
    </Box>
  );
};

const GanttRow = ({
  highlightedLoad,
  minTimeMs,
  onHighlightLoad,
  onSelectTruck,
  row,
  rowDimmed,
  span,
}: {
  highlightedLoad: HighlightedLoad | null;
  minTimeMs: number;
  onHighlightLoad: (load: HighlightedLoad) => void;
  onSelectTruck: (jobId: string) => void;
  row: ReplayRow;
  rowDimmed: boolean;
  span: number;
}) => {
  const theme = useTheme();
  const seenLoadSequences = new Set<number>();

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: ROW_HEIGHT_IN_PX,
        opacity: rowDimmed ? DIMMED_OPACITY : 1,
      }}
    >
      <Box
        component="button"
        onClick={() => onSelectTruck(row.key)}
        // Don't let a label click also scrub the timeline.
        onPointerDown={(event) => event.stopPropagation()}
        sx={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          flex: `0 0 ${LABEL_WIDTH_IN_PX}px`,
          overflow: 'hidden',
          pr: 1.5,
          textAlign: 'right',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.brandV2.colors.treadBlack,
            display: 'block',
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.label.truckNumber}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.brandV2.colors.treadGray3,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.label.vendor}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.brandV2.colors.treadGray9,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: 0.5,
          flex: 1,
          height: TRACK_HEIGHT_IN_PX,
          mr: `${TRACK_RIGHT_GUTTER_IN_PX}px`,
          position: 'relative',
        }}
      >
        {row.segments.map((segment, index) => {
          const isFirstOfLoad =
            segment.loadSequence !== null && !seenLoadSequences.has(segment.loadSequence);
          if (segment.loadSequence !== null) {
            seenLoadSequences.add(segment.loadSequence);
          }

          const isHighlighted =
            highlightedLoad !== null &&
            highlightedLoad.jobId === row.key &&
            highlightedLoad.loadId === segment.loadId;

          return (
            <GanttBar
              key={index}
              dimmed={highlightedLoad !== null && !isHighlighted}
              isFirstOfLoad={isFirstOfLoad}
              minTimeMs={minTimeMs}
              onClick={() => {
                if (segment.loadId) {
                  onHighlightLoad({ jobId: row.key, loadId: segment.loadId });
                }
              }}
              segment={segment}
              span={span}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const GanttBar = ({
  dimmed,
  isFirstOfLoad,
  minTimeMs,
  onClick,
  segment,
  span,
}: {
  dimmed: boolean;
  isFirstOfLoad: boolean;
  minTimeMs: number;
  onClick: () => void;
  segment: ReplaySegment;
  span: number;
}) => {
  const theme = useTheme();
  const leftPct = span > 0 ? ((segment.startMs - minTimeMs) / span) * 100 : 0;
  const rawWidthPct = span > 0 ? ((segment.endMs - segment.startMs) / span) * 100 : 0;
  const widthPct = Math.max(rawWidthPct, MIN_BAR_WIDTH_PCT);
  const showMinutes = rawWidthPct >= MIN_BAR_PCT_FOR_MINUTES && segment.durationMin > 0;
  // AT_PICKUP (amber) and IDLE (yellow) are light fills, so their minute labels
  // need dark text; the blue/green/hatched buckets use white.
  const isLightBucket = segment.bucket === 'AT_PICKUP' || segment.bucket === 'IDLE';
  const textColor = isLightBucket ? theme.brandV2.colors.treadBlack : '#FFFFFF';

  return (
    <Box
      {...(segment.loadId ? { [LOAD_BAR_ATTRIBUTE]: '' } : {})}
      onClick={onClick}
      sx={{
        alignItems: 'center',
        background: segmentBackground(segment.bucket),
        color: textColor,
        cursor: segment.loadId ? 'pointer' : 'default',
        display: 'flex',
        fontSize: 10,
        fontWeight: 700,
        height: '100%',
        justifyContent: 'center',
        left: `${leftPct}%`,
        opacity: dimmed ? DIMMED_OPACITY : 1,
        overflow: 'visible',
        position: 'absolute',
        top: 0,
        width: `${widthPct}%`,
      }}
    >
      {isFirstOfLoad && segment.loadSequence !== null ? (
        <Box
          sx={{
            backgroundColor: theme.brandV2.colors.treadBlack,
            borderRadius: '3px',
            color: '#FFFFFF',
            fontSize: 8.5,
            left: 0,
            lineHeight: '13px',
            position: 'absolute',
            px: 0.5,
            top: -9,
          }}
        >
          {t('truck_replay.load_badge', { sequence: segment.loadSequence })}
        </Box>
      ) : null}
      {showMinutes ? segment.durationMin : null}
    </Box>
  );
};

const Legend = () => {
  const theme = useTheme();
  const buckets: ReplayBucket[] = [
    'EN_ROUTE',
    'AT_PICKUP',
    'AT_DROPOFF',
    'IDLE',
    'UNKNOWN',
  ];

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        pt: 1.5,
      }}
    >
      {buckets.map((bucket) => (
        <Box key={bucket} sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <Box
            sx={{
              background: segmentBackground(bucket),
              borderRadius: '3px',
              height: 13,
              width: 13,
            }}
          />
          <Typography variant="caption" sx={{ color: theme.brandV2.colors.treadGray1 }}>
            {t(BUCKET_LABEL_KEYS[bucket])}
          </Typography>
        </Box>
      ))}
      <Typography
        variant="caption"
        sx={{ color: theme.brandV2.colors.treadGray3, ml: 'auto' }}
      >
        {t('truck_replay.legend_minutes')}
      </Typography>
    </Box>
  );
};

export default TruckReplayGantt;
